import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { getFirstVisiblePath } from "./config/menuConfig";

import ErrorBoundary from "./components/ErrorBoundary";
// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import InvoicesPage from "./pages/InvoicesPage";
import AccountsPage from "./pages/AccountsPage";
import CostCentersPage from "./pages/CostCentersPage";
import ReportsPage from "./pages/ReportsPage";
import BankMovementsPage from "./pages/BankMovementsPage";
import CardMovementsPage from "./pages/CardMovementsPage";
import TesoreriaAnagrafichePage from "./pages/TesoreriaAnagrafichePage";
import PaymentMethodsPage from "./pages/PaymentMethodsPage";
import BilancioCompetenzaPage from "./pages/BilancioCompetenzaPage";
import ScadenzeRicorrentiPage from "./pages/ScadenzeRicorrentiPage";
import AppartamentiPage from "./pages/AppartamentiPage";
import AlloggiDipendentiPage from "./pages/AlloggiDipendentiPage";
import ControlliAppartamentiPage from "./pages/ControlliAppartamentiPage";
import ScadenziarioUnificatoPage from "./pages/ScadenziarioUnificatoPage";
import ReportAffittiPage from "./pages/ReportAffittiPage";
import GestioneUtentiPage from "./pages/GestioneUtentiPage";
import AttivitaUtentiPage from "./pages/AttivitaUtentiPage";
import TesoreriaPage from "./pages/TesoreriaPage";
// Automezzi Pages
import AutomezziPage from "./pages/AutomezziPage";
import AutomezziAssicurazioniPage from "./pages/AutomezziAssicurazioniPage";
import AutomezziBolloPage from "./pages/AutomezziBolloPage";
import AutomezziKmPage from "./pages/AutomezziKmPage";
import AutomezziLocazioniPage from "./pages/AutomezziLocazioniPage";
import AutomezziManutenzioniPage from "./pages/AutomezziManutenzioniPage";
import AutomezziCarburantiPage from "./pages/AutomezziCarburantiPage";
import ReportManutenzioniPage from "./pages/ReportManutenzioniPage";
// Tasks Page
import TasksPage from "./pages/TasksPage";
// Bollette Page
import BollettePage from "./pages/BollettePage";
// Asset Page
import AssetPage from "./pages/AssetPage";
import AssetSpostamentiPage from "./pages/AssetSpostamentiPage";
import DDTPage from "./pages/DDTPage";
// Cash Flow Dashboard
import CashFlowDashboardPage from "./pages/CashFlowDashboardPage";
// Ammortamenti Pages
import CategorieImmobilizzazioniPage from "./pages/CategorieImmobilizzazioniPage";
import PianoAmmortamentiPage from "./pages/PianoAmmortamentiPage";
import RipartizioneCostiPage from "./pages/RipartizioneCostiPage";
// Riconciliazioni Page
import RiconciliazioniPage from "./pages/RiconciliazioniPage";
// Movimenti Patrimoniali Page
import MovimentiPatrimonialiPage from "./pages/MovimentiPatrimonialiPage";
import UsciteCassaPage from "./pages/UsciteCassaPage";
// Projects/Task Management Pages
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
// Costi Extra-Fattura
import CostiExtraPage from "./pages/CostiExtraPage";
// Budget Management
import BudgetPage from "./pages/BudgetPage";
// Piani Leasing
import PianiLeasingPage from "./pages/PianiLeasingPage";
import ManodoperaCdcPage from "./pages/ManodoperaCdcPage";
import BudgetConfrontoPage from "./pages/BudgetConfrontoPage";
import IndiciPage from "./pages/IndiciPage";
// Analytics & AI
import AnalyticsPage from "./pages/AnalyticsPage";
import AuditTrailPage from "./pages/AuditTrailPage";
import ApprovalWorkflowPage from "./pages/ApprovalWorkflowPage";
import CalendarioScadenzePage from "./pages/CalendarioScadenzePage";
// Produzione
import {
  ProduzioneDashboardPage,
  ProduzioneCommessePage,
  ProduzioneCommessaDetailPage,
  ProduzioneStabilimentiPage,
  ProduzioneMaterialePage,
  ProduzioneOrePage,
  ProduzioneFornitoriPage,
  ProduzioneTipiAttivitaPage,
  ProduzioneCronoprogrammaPage
} from "./pages/Produzione";
// Super Admin Pages
import SuperAdminLoginPage from "./pages/SuperAdminLoginPage";
import SuperAdminDashboardPage from "./pages/SuperAdminDashboardPage";
import BackupPage from "./pages/BackupPage";
// Tenant Pages
import TenantLoginPage from "./pages/TenantLoginPage";
import TenantLayout from "./components/TenantLayout";
import Layout from "./components/Layout";

import { I18nProvider } from "./contexts/I18nContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Tenant Context - fornisce info sul tenant corrente
export const TenantContext = createContext(null);

export const useTenant = () => {
  return useContext(TenantContext);
};

// API instance
export const api = axios.create({
  baseURL: API,
});

// ==================== TOKEN REFRESH ====================
// Rinnova il token automaticamente ogni 4 ore per evitare scadenze silenziose
let refreshInterval = null;

export function startTokenRefresh() {
  if (refreshInterval) clearInterval(refreshInterval);
  refreshInterval = setInterval(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const resp = await axios.post(`${API}/tenant/auth/refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resp.data?.token) {
        localStorage.setItem('token', resp.data.token);
        api.defaults.headers.common["Authorization"] = `Bearer ${resp.data.token}`;
      }
    } catch {
      // Token scaduto o invalido, il 401 interceptor gestirà il logout
    }
  }, 24 * 60 * 60 * 1000); // ogni 24 ore
}

export function stopTokenRefresh() {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
}

// Avvia refresh se già autenticato
if (localStorage.getItem('token')) {
  startTokenRefresh();
}

// Interceptor: redirect al login SOLO quando il backend segnala token scaduto/invalido.
// Altri 401 (es. permessi mancanti serializzati come 401 da endpoint mal codificati)
// vengono passati come errori normali - così l'utente non viene sloggato senza motivo.
let isRedirecting = false;
const TOKEN_INVALID_MESSAGES = [
  'token scaduto',
  'token non valido',
  'token non valido per tenant',
  'signature has expired',
];
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401 && !isRedirecting) {
      const detail = (error.response.data?.detail || '').toString().toLowerCase();
      const isTokenIssue = TOKEN_INVALID_MESSAGES.some(m => detail.includes(m));
      if (!isTokenIssue) {
        // 401 diverso da token scaduto → lascia l'errore ai gestori del componente,
        // che mostreranno un toast ("Sessione scaduta — effettua nuovamente il login").
        // NON forziamo il logout: l'utente resta sulla pagina e può riprovare.
        return Promise.reject(error);
      }
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/login')) {
        isRedirecting = true;
        stopTokenRefresh();
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('currentTenant');
        localStorage.removeItem('tenant');
        localStorage.removeItem('impersonatedTenant');
        localStorage.removeItem('superAdminToken');
        const tenant = currentPath.split('/')[1];
        if (tenant === 'admin') {
          window.location.href = '/admin';
        } else if (tenant) {
          window.location.href = `/${tenant}/login`;
        } else {
          window.location.href = '/';
        }
        setTimeout(() => { isRedirecting = false; }, 3000);
      }
    }
    return Promise.reject(error);
  }
);

// Tenant Routes Wrapper - verifica autenticazione per tenant
function TenantRoutes() {
  const { tenant } = useParams();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedTenant = localStorage.getItem("currentTenant");
    const storedUser = localStorage.getItem("user");
    const storedTenantInfo = localStorage.getItem("tenant");
    
    // Verifica che il token sia per questo tenant
    if (token && storedTenant === tenant) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      setIsAuthenticated(true);
      if (storedUser) setUser(JSON.parse(storedUser));
      if (storedTenantInfo) setTenantInfo(JSON.parse(storedTenantInfo));
    }
    setLoading(false);
  }, [tenant]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={`/${tenant}/login`} replace />;
  }

  return (
    <TenantContext.Provider value={{ tenant, user, tenantInfo }}>
        <TenantLayout>
          <Routes>
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="fatture" element={<InvoicesPage />} />
          <Route path="movimenti" element={<BankMovementsPage />} />
          <Route path="movimenti-carte" element={<CardMovementsPage />} />
          <Route path="riconciliazioni" element={<RiconciliazioniPage />} />
          <Route path="tesoreria-anagrafiche" element={<TesoreriaAnagrafichePage />} />
          <Route path="conti" element={<AccountsPage />} />
          <Route path="centri-costo" element={<CostCentersPage />} />
          <Route path="modalita-pagamento" element={<PaymentMethodsPage />} />
          <Route path="scadenze-ricorrenti" element={<ScadenzeRicorrentiPage />} />
          <Route path="appartamenti" element={<AppartamentiPage />} />
          <Route path="alloggi-dipendenti" element={<AlloggiDipendentiPage />} />
          <Route path="controlli-appartamenti" element={<ControlliAppartamentiPage />} />
          <Route path="bilancio-competenza" element={<BilancioCompetenzaPage />} />
          <Route path="movimenti-patrimoniali" element={<MovimentiPatrimonialiPage />} />
          <Route path="uscite-cassa" element={<UsciteCassaPage />} />
          <Route path="ripartizione-costi" element={<RipartizioneCostiPage />} />
          <Route path="scadenziario" element={<ScadenziarioUnificatoPage />} />
          <Route path="tesoreria" element={<TesoreriaPage />} />
          <Route path="report-affitti" element={<ReportAffittiPage />} />
          <Route path="gestione-utenti" element={<GestioneUtentiPage />} />
          <Route path="attivita-utenti" element={<AttivitaUtentiPage />} />
          <Route path="report" element={<ReportsPage />} />
          {/* Automezzi Routes */}
          <Route path="automezzi" element={<AutomezziPage />} />
          <Route path="automezzi/assicurazioni" element={<AutomezziAssicurazioniPage />} />
          <Route path="automezzi/bollo" element={<AutomezziBolloPage />} />
          <Route path="automezzi/km" element={<AutomezziKmPage />} />
          <Route path="automezzi/locazioni" element={<AutomezziLocazioniPage />} />
          <Route path="automezzi/manutenzioni" element={<AutomezziManutenzioniPage />} />
          <Route path="automezzi/carburanti" element={<AutomezziCarburantiPage />} />
          <Route path="automezzi/report-manutenzioni" element={<ReportManutenzioniPage />} />
          {/* Asset Routes */}
          <Route path="asset" element={<AssetPage />} />
          <Route path="asset/spostamenti" element={<AssetSpostamentiPage />} />
          <Route path="asset/ddt" element={<DDTPage />} />
          {/* Ammortamenti Routes */}
          <Route path="categorie-immobilizzazioni" element={<CategorieImmobilizzazioniPage />} />
          <Route path="piano-ammortamenti" element={<PianoAmmortamentiPage />} />
          {/* Costi Extra-Fattura */}
          <Route path="costi-extra" element={<CostiExtraPage />} />
          <Route path="piani-leasing" element={<PianiLeasingPage />} />
          <Route path="manodopera-cdc" element={<ManodoperaCdcPage />} />
          {/* Budget Management Routes */}
          <Route path="budget" element={<BudgetPage />} />
          <Route path="budget/confronto" element={<BudgetConfrontoPage />} />
          <Route path="budget/indici" element={<IndiciPage />} />
          {/* Produzione Routes */}
          <Route path="produzione" element={<ProduzioneDashboardPage />} />
          <Route path="produzione/commesse" element={<ProduzioneCommessePage />} />
          <Route path="produzione/commesse/:commessaId" element={<ProduzioneCommessaDetailPage />} />
          <Route path="produzione/stabilimenti" element={<ProduzioneStabilimentiPage />} />
          <Route path="produzione/materiale" element={<ProduzioneMaterialePage />} />
          <Route path="produzione/ore" element={<ProduzioneOrePage />} />
          <Route path="produzione/fornitori" element={<ProduzioneFornitoriPage />} />
          <Route path="produzione/tipi-attivita" element={<ProduzioneTipiAttivitaPage />} />
          <Route path="produzione/cronoprogramma" element={<ProduzioneCronoprogrammaPage />} />
          {/* Tasks */}
          <Route path="compiti" element={<TasksPage />} />
          {/* Projects/Task Management */}
          <Route path="progetti" element={<ProjectsPage />} />
          <Route path="progetti/:projectId" element={<ProjectDetailPage />} />
          {/* Bollette */}
          <Route path="bollette" element={<BollettePage />} />
          {/* Cash Flow Dashboard */}
          <Route path="cash-flow" element={<CashFlowDashboardPage />} />
          {/* Analytics & AI */}
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="audit-trail" element={<AuditTrailPage />} />
          <Route path="approvazioni" element={<ApprovalWorkflowPage />} />
          <Route path="calendario-scadenze" element={<CalendarioScadenzePage />} />
          {/* Default redirect alla prima voce di menu visibile */}
          <Route path="" element={<SmartRedirect tenant={tenant} user={user} />} />
          <Route path="*" element={<SmartRedirect tenant={tenant} user={user} />} />
        </Routes>
      </TenantLayout>
    </TenantContext.Provider>
  );
}

// Redirect dinamico alla prima voce di menu visibile per l'utente
function SmartRedirect({ tenant, user }) {
  const permissions = user?.permissions || {};
  const firstPath = getFirstVisiblePath(permissions);
  return <Navigate to={`/${tenant}/${firstPath}`} replace />;
}

// Homepage selector - redirect to admin or show tenant selector
function HomePage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <h1 className="text-3xl font-bold mb-4">Gestione Contabile</h1>
        <p className="text-gray-600 mb-8">Piattaforma multi-azienda per la gestione contabile e finanziaria</p>
        <div className="space-y-4">
          <a 
            href="/admin" 
            className="block w-full bg-purple-600 text-white py-3 px-6 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Accesso Amministratore Piattaforma
          </a>
          <p className="text-sm text-gray-500">
            Per accedere alla tua azienda, usa l'URL:<br/>
            <code className="bg-gray-200 px-2 py-1 rounded">/nome-azienda/login</code>
          </p>
        </div>
      </div>
    </div>
  );
}

// Legacy Auth - mantiene compatibilità con vecchio sistema
function LegacyApp() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const currentTenant = localStorage.getItem("currentTenant");
    
    // Se c'è un tenant, non usare il legacy auth
    if (currentTenant) {
      setLoading(false);
      return;
    }
    
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      api.get("/auth/me")
        .then((res) => setUser(res.data))
        .catch(() => {
          localStorage.removeItem("token");
          delete api.defaults.headers.common["Authorization"];
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", res.data.token);
    api.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
    setUser(res.data.user);
    return res.data;
  };

  const register = async (data) => {
    const res = await api.post("/auth/register", data);
    localStorage.setItem("token", res.data.token);
    api.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete api.defaults.headers.common["Authorization"];
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, register, logout }}>
      <Routes>
        <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/" />} />
        <Route path="/register" element={!user ? <RegisterPage /> : <Navigate to="/" />} />
        <Route
          path="/*"
          element={
            user ? (
              <Layout>
                <Routes>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/fatture" element={<InvoicesPage />} />
                  <Route path="/movimenti" element={<BankMovementsPage />} />
                  <Route path="/movimenti-carte" element={<CardMovementsPage />} />
                  <Route path="/conti" element={<AccountsPage />} />
                  <Route path="/centri-costo" element={<CostCentersPage />} />
                  <Route path="/modalita-pagamento" element={<PaymentMethodsPage />} />
                  <Route path="/scadenze-ricorrenti" element={<ScadenzeRicorrentiPage />} />
                  <Route path="/appartamenti" element={<AppartamentiPage />} />
                  <Route path="/controlli-appartamenti" element={<ControlliAppartamentiPage />} />
                  <Route path="/bilancio-competenza" element={<BilancioCompetenzaPage />} />
                  <Route path="/uscite-cassa" element={<UsciteCassaPage />} />
                  <Route path="/ripartizione-costi" element={<RipartizioneCostiPage />} />
                  <Route path="/scadenziario" element={<ScadenziarioUnificatoPage />} />
                  <Route path="/report-affitti" element={<ReportAffittiPage />} />
                  <Route path="/gestione-utenti" element={<GestioneUtentiPage />} />
                  <Route path="/report" element={<ReportsPage />} />
                  {/* Automezzi Routes */}
                  <Route path="/automezzi" element={<AutomezziPage />} />
                  <Route path="/automezzi/assicurazioni" element={<AutomezziAssicurazioniPage />} />
                  <Route path="/automezzi/bollo" element={<AutomezziBolloPage />} />
                  <Route path="/automezzi/km" element={<AutomezziKmPage />} />
                  <Route path="/automezzi/locazioni" element={<AutomezziLocazioniPage />} />
                  <Route path="/automezzi/manutenzioni" element={<AutomezziManutenzioniPage />} />
                  <Route path="/automezzi/carburanti" element={<AutomezziCarburantiPage />} />
                  <Route path="/automezzi/report-manutenzioni" element={<ReportManutenzioniPage />} />
                </Routes>
              </Layout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
      </Routes>
    </AuthContext.Provider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <I18nProvider>
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <Routes>
            {/* Homepage */}
            <Route path="/" element={<HomePage />} />
            
            {/* Super Admin Routes */}
            <Route path="/admin" element={<SuperAdminLoginPage />} />
            <Route path="/admin/login" element={<SuperAdminLoginPage />} />
            <Route path="/admin/dashboard" element={<SuperAdminDashboardPage />} />
            <Route path="/admin/backups" element={<BackupPage />} />
            
            {/* Tenant Login */}
            <Route path="/:tenant/login" element={<TenantLoginPage />} />
            
            {/* Tenant Routes (authenticated) */}
            <Route path="/:tenant/*" element={<TenantRoutes />} />
            
            {/* Legacy routes (backwards compatibility) */}
            <Route path="/login" element={<LegacyApp />} />
            <Route path="/register" element={<LegacyApp />} />
          </Routes>
        </BrowserRouter>
      </I18nProvider>
    </ErrorBoundary>
  );
}

export default App;
